import type { Context, ReactNode } from 'react';
import { useEffect } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ZodType, ZodTypeDef } from 'zod';

import { captureSchemaValidationError } from '@utility/checkout-utils';

import { JWT_CONTEXT_DATA } from './jwt.shared-mocks';
import type { JwtContextData, JwtContextProviderProps } from './jwt.types';
import { getJwtPayload } from './jwt.utils';

/**
 * This factory returns `JwtContextProvider`, `useJwtContext`, `withJwtContextDecorator` and `withJwtContextProvider`;
 * Usage:
 * ````
 *   import { jwtContextFactory } from '@state/checkout-contexts';
 *   const { JwtContextProvider, useJwtContext, withJwtContextDecorator, withJwtContextProvider } = jwtContextFactory<YOUR_JWT_HERE>();
 *
 *  export { JwtContextProvider, useJwtContext, withJwtContextDecorator, withJwtContextProvider };
 * ````
 */
const jwtContextFactory = <T,>({
  schema,
}: { schema?: ZodType<T, ZodTypeDef, T> } = {}) => {
  const JwtContext = createContext<JwtContextData<T> | null>(null);

  const useJwtContext = () => {
    const context = useContext(JwtContext);

    if (!context) {
      throw new Error(
        'useJwtContext must be used within a <JwtContextProvider>'
      );
    }

    return context;
  };

  const JwtContextProvider = ({
    children,
    tokenKey,
  }: JwtContextProviderProps) => {
    const { payload, token, tokenError } = useMemo(
      () => getJwtPayload<T>(tokenKey),
      [tokenKey]
    );

    useEffect(() => {
      if (schema) {
        const result = schema.safeParse(payload);

        if (!result.success) {
          const validationError = result.error;

          captureSchemaValidationError(validationError, {
            contexts: {
              response: {
                apiResponse: result,
              },
              error: {
                schemaError: validationError.issues,
              },
            },
          });
        }
      }
    }, [payload]);

    return (
      <JwtContext.Provider
        value={{
          payload,
          token,
          tokenError,
        }}
      >
        {children}
      </JwtContext.Provider>
    );
  };

  /**
   * helper to be used when wrapping a component with JwtContextProvider is needed, out of JSX structures eg. in functions
   */
  const withJwtContextProvider = ({ children }: { children: ReactNode }) => (
    <JwtContextProvider>{children}</JwtContextProvider>
  );

  /**
   * helper to be used for proper displaying stories in Storybook
   */
  const withJwtContextDecorator = (data?: Partial<JwtContextData<T>>) => ({
    context: JwtContext as Context<JwtContextData<T>>,
    contextValue: ({ args }: { args: Partial<JwtContextData<T>> }) =>
      JWT_CONTEXT_DATA({ ...data, ...args }),
  });

  return {
    JwtContextProvider,
    useJwtContext,
    withJwtContextProvider,
    withJwtContextDecorator,
  };
};

export { jwtContextFactory };
